import React from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Button, Icon} from '@lightricks/react-design-system';
import type {CampaignApprovalStep} from '@/types/campaign';
import {CONTENT_SUBMISSION_KIND} from '@/types/contentSubmission';
import MultiSelectionOptionsList from '@/components/filters/multi-selection-options-list';
import SideDrawer from '@/components/side-drawer';
import useContentSubmissionsStore, {
  contentSubmissionsActions,
} from '@/stores/content/content-submissions/contentSubmissionsStore';
import {CONTENT_TYPE_OPTIONS} from '@/stores/content/content-submissions/filterConfig';
import useContentSubmissionStatusOptions from '../../hooks/useContentSubmissionStatusOptions';
import type {CampaignCreator} from '../../types/campaignCreator';
import CreatorAutocomplete from '../creator-autocomplete';
import styles from './FilterDrawer.module.scss';
import FilterDrawerFooter from './FilterDrawerFooter';
import FilterSection from './FilterSection';

type FilterDrawerProps = {
  campaignCreators: CampaignCreator[];
  campaignSocialNetwork?: string | null;
  campaignApprovalSteps?: CampaignApprovalStep[];
  onApply?: () => void;
  onClearAll?: () => void;
  onContentTypeChange?: (value: string) => void;
  onStatusChange?: (value: string) => void;
  onCreatorChange?: (context: {creator_id?: string}) => void;
};

function FilterDrawer({
  campaignCreators,
  campaignSocialNetwork,
  campaignApprovalSteps,
  onApply,
  onClearAll,
  onContentTypeChange,
  onStatusChange,
  onCreatorChange,
}: FilterDrawerProps) {
  const [isOpen, filters] = useContentSubmissionsStore(
    useShallow((state) => [state.isFilterDrawerOpen, state.filters])
  );

  const {statusOptions} = useContentSubmissionStatusOptions(
    campaignApprovalSteps ?? []
  );

  const contentTypeOptions = CONTENT_TYPE_OPTIONS.filter(
    (option) =>
      !(
        option.id === CONTENT_SUBMISSION_KIND.MIXED_MEDIA &&
        !campaignSocialNetwork?.includes('instagram')
      )
  );

  const hasActiveFilters =
    contentSubmissionsActions.getActiveFilterCount(filters) > 0;

  return (
    <SideDrawer
      testID="content-submissions-filter-drawer"
      isOpen={isOpen}
      onClose={contentSubmissionsActions.closeFilterDrawer}
      drawerClassName={styles.drawer}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <Button
            className={styles.closeButton}
            size="small"
            appearance="neutral"
            mode="plain"
            onClick={contentSubmissionsActions.closeFilterDrawer}
          >
            <Icon
              size="small"
              appearance="neutral"
              name="Actions-Close-Normal"
            />
          </Button>
        </div>

        <div className={styles.content}>
          <FilterSection title="Talent">
            <CreatorAutocomplete
              testID="talent-filter"
              campaignCreators={campaignCreators}
              selectedCreator={filters.talent}
              onChange={(creator) => {
                contentSubmissionsActions.setTalent(creator);
                onCreatorChange?.({creator_id: creator?.id});
              }}
            />
          </FilterSection>

          <div className={styles.separator} />

          <FilterSection title="Content Type">
            <MultiSelectionOptionsList
              options={contentTypeOptions}
              selectedOptions={filters.contentType}
              onChange={(option) => {
                contentSubmissionsActions.toggleFilterOption(
                  'contentType',
                  option
                );
                onContentTypeChange?.(option.id as string);
              }}
              scrollable={false}
            />
          </FilterSection>

          <div className={styles.separator} />

          <FilterSection title="Status">
            <MultiSelectionOptionsList
              options={statusOptions}
              selectedOptions={filters.status}
              onChange={(option) => {
                contentSubmissionsActions.toggleFilterOption('status', option);
                onStatusChange?.(option.id as string);
              }}
              scrollable={false}
            />
          </FilterSection>
        </div>

        <FilterDrawerFooter
          clearAllDisabled={!hasActiveFilters}
          onClearAll={() => {
            onClearAll?.();
            contentSubmissionsActions.clearFilters();
          }}
          onApply={() => {
            onApply?.();
            contentSubmissionsActions.applyFilters();
          }}
        />
      </div>
    </SideDrawer>
  );
}

export default FilterDrawer;
